import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  NgZone,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { LMListComponent, LMListSelectionEvent } from '@list/lm-list.component';
import { LMContextService, LMRouteComponentService } from '@services/index';
import { AppConstants } from '@utils/app.constants';
import { LMLookupSelectionEvent } from './lm-lookup-selection.event';

@Component({
  templateUrl: './lm-lookup-dialog.component.html',
  providers: [ LMRouteComponentService ]
})
export class LMLookupDialogComponent implements AfterViewChecked {

  @ViewChild(forwardRef(() => LMListComponent)) selectList: LMListComponent;

  @HostListener('keyup', ['$event']) onKeyUp(event) {
    if (event.which === 13 && event.srcElement.nodeName !== 'BUTTON' && event.srcElement.nodeName !== 'INPUT') {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault();
      if (this.selectList.getSelectedRows().length > 0) {
        // if a record is selected, then enter should be like clicking the ok button
        this.elementRef.nativeElement.parentElement.parentElement.parentElement.parentNode.querySelector('button[id="' + this.buttonIds.ok + '"]').click();
      }
    }
  }

  /**
   *
   * @type {EventEmitter<LMLookupSelectionEvent>}
   */
  @Output() lookupSelection = new EventEmitter<LMLookupSelectionEvent>();

  /**
   *
   * @type {EventEmitter<string>}
   */
  @Output() titleChanged = new EventEmitter<string>();
  @Output() navigatingToolbarEvent = new EventEmitter<LMToolbarButtonEvent>();

  @Input() listUrlInfo: LMListUrlInfo;

  /**
   * The list spec of the select list used to Select a value for our
   * select field.
   */
  @Input() listSpec: LMListSpec;

  /**
   * The SelectRequestData containing what's needed to run the select and
   * page appropriately over it's records.
   */
  @Input() selectRequestData: SelectRequestData;
  @Input() buttonIds: {ok: string, cancel: string};
  @Input() cubeRelSel: boolean;
  @Input() allowMultiSelections: boolean = false;

  private executeFocus = false;

  constructor(
    private lmContextService: LMContextService,
    private elementRef: ElementRef,
    private changeDetectionRef: ChangeDetectorRef,
    private renderer: Renderer2,
    private zone: NgZone) {}

  ngAfterViewChecked(): void {
    if (this.executeFocus) {
      this.executeFocus = false;
      this.handleFocus();
    }
  }

  /**
   * Re-emit the row selection event.
   * @param listSelectionEvent
   */
  onRowsSelected(listSelectionEvent: LMListSelectionEvent) {
    const lookupSelectionEvent = new LMLookupSelectionEvent(this, listSelectionEvent.selectedRows);
    // LMCLIENT-30270: error thrown for .querySelector('#' + buttonId) when buttonId begins with a number
    const okButton = this.elementRef.nativeElement.parentElement.parentElement.parentElement.parentNode.querySelector('button[id="' + this.buttonIds.ok + '"]');

    if (!this.selectRequestData.allowMultiSelections) {
      if (lookupSelectionEvent.rows && lookupSelectionEvent.rows.length > 1) {
        // Finding the button via dom lookup until Soho exposes the buttons in a soho modal. https://github.com/infor-design/enterprise-ng/issues/467
        this.renderer.setAttribute(okButton, 'disabled', 'true');
      } else {
        this.renderer.removeAttribute(okButton, 'disabled');
      }
    }

    this.lookupSelection.emit(lookupSelectionEvent);
  }

  /**
   * Re-emit title changed event from lm-list.component to that the apply function has a chance
   * to subscribe to it and in turn update the dialogs title.
   */
  onTitleChanged(newTitle: string) {
    this.titleChanged.emit(newTitle);
  }

  onToolbarButtonEvent(event: LMToolbarButtonEvent) {
    const id = event.actionReference.id.toUpperCase();
    if (id.indexOf(AppConstants.ACTION_TYPE_CREATE) > -1 ||
        id.indexOf(AppConstants.ACTION_TYPE_UPDATE) > -1 ) {
      this.navigatingToolbarEvent.emit(event);
    }
  }

  onListDisplayed() {
    this.executeFocus = true;
  }

  triggerDetectChanges() {
    this.changeDetectionRef.markForCheck();
  }

  handleFocus() {
    // This needs to be in a timeout to ensure that the list in the modal is displayed before attempting to focus
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        // does the child list have a search form displayed initially or always
        if (this.selectList.hasSearchForm && this.selectList.showSearchForm) {
          this.selectList.searchFormComponent.elementRef.nativeElement.querySelectorAll('input')[0].focus();
          return;
        }

        // focus first row, first cell
        this.selectList.listContentComponent?.listGrid?.sohoDataGridComponent.setActiveCell(0, 0);
      }, 2);
    });
  }
}
